import { delay } from 'redux-saga/effects';
import { call, cancel, cancelled, fork, put, race, select, take } from 'redux-saga/effects';
import { alarmReceived } from 'actions/alarmActions';
import { timelineDataReceived } from 'actions/timelineActions';
import { getMonitoredDeviceIds } from 'selectors/monitoringSelectors';
import { getTimelineMessagesDate } from 'selectors/timelineSelectors';
import { postPoll } from 'api';
import { PollData, AlarmMap, Alarm, DeviceMap } from 'types';
import { logout, instantMessagesReceived } from 'actions/shellActions';
import { devicesLoadRequest } from 'actions/deviceActions';
import { deviceGroupsLoadRequest } from 'actions/deviceGroupActions';
import { makeAbortController } from 'api/fetch';
import { getDevices } from 'selectors/deviceSelectors';

export function* pollLoginWatcher() {
    // Wait for a user to log in; then run the pollSaga until the user logs out.
    while (true) {
        yield take('LOGIN_SUCCESS');
        const task = yield fork(pollSaga);
        yield take('LOGOUT');
        yield cancel(task);
    }
}

export function* pollSaga() {
    let consecutiveFails = 0;
    let abortController: AbortController;

    while (true) {
        // Determine which devices the user is currently monitoring
        const deviceIds = yield select(getMonitoredDeviceIds);

        abortController = yield call(makeAbortController);

        let pollData: PollData;
        try {
            const { _pollData, assignStream, restoreLayout } = yield race({
                _pollData: call(postPoll, deviceIds, abortController.signal), // returns when an alarm and/or camera messages are available
                assignStream: take('ASSIGN_STREAM'),  // triggered when a camera stream is assigned to a monitoring cell
                restoreLayout: take('RESTORE_LAYOUT'), // sent after login if the user had a previous session saved
            });
            
            // Make a new poll request with a new list of device IDs
            if (assignStream || restoreLayout) {
                abortController.abort();
                continue;
            }

            consecutiveFails = 0;
            pollData = _pollData; // HACK: can't deconstruct into an existing variable... transfer temporary variable into the real thing
        }
        catch (err) {
            // Bail out if this task was cancelled (i.e. the user logged out)
            if (yield cancelled()) {
                abortController.abort();
                return;
            }

            // Only show the error the first time... no use filling up the logs with the same error
            if (++consecutiveFails === 1) {
                yield call([ console, 'error' ], err);
            }

            // If something bad happened to the user session (e.g. server rebooted, network connection was down for too long, etc)
            // then log out the client. (No need to try to logout on the server because we know the session is already invalid).
            if ((err as Error).message === 'WC_E_INVALID_SESSION') {
                yield put(logout());
                return;
            }

            // Exponential backoff. Max is 30 seconds
            const maxWait = 30000;
            const backoffCoefficient = 500;
            const waitTime = Math.min(consecutiveFails * consecutiveFails * backoffCoefficient, maxWait);
            yield delay(waitTime);
            continue;
        }

        // Handle the data in a different task so we can poll again right away
        yield fork(handlePollResultsSaga, pollData);
    }
}

export function* handlePollResultsSaga(pollData: PollData) {
    const { alarms, cameraMessages, messages, refreshDevices } = pollData;
    if (alarms) {
        yield put(alarmReceived(alarms));
    }
    if (cameraMessages) {

        const utcDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()); // get the current date
        const deviceIds = Object.keys(cameraMessages);
        
        for (let j = 0; j < deviceIds.length; j++) {
            const deviceId = deviceIds[j].valueOf();
            const previousUtcTime = yield select(getTimelineMessagesDate, { deviceId });

            var isSameDate = false;
            if (!!previousUtcTime) {
                const timelineUTCTime = new Date(previousUtcTime.getFullYear(), previousUtcTime.getMonth(), previousUtcTime.getDate());
                isSameDate = timelineUTCTime.toString() === utcDate.toString();
            }

            if (!isSameDate) { // If the devices timeline is not set to the current date, it is showing historical data, drop the poll data for this device
                delete cameraMessages[deviceId];
             }
        }

        const actions = Object.keys(cameraMessages).map(deviceId =>
            timelineDataReceived(cameraMessages[deviceId], { deviceId, utcDate: null, append: true })
        );
        for (let i = 0; i < actions.length; i++) {
            yield put(actions[i]);
        }

        const deviceMap: DeviceMap = yield select(getDevices);

        const alarms = Object.keys(cameraMessages).reduce((array, deviceId) => [
            ...array,
            ...cameraMessages[deviceId].filter(cm => !!cm.a).map(cm => ({
                alarmId: cm.a,
                deviceId: ((deviceMap[deviceId] || {}).deviceIndex || 0).toString(), // convert deviceId to deviceIndex string
                policyId: cm.p,
                time: cm.t,
            }) as Alarm),
        ], []);

        if (alarms.length) {
            const alarmMap = alarms.reduce((map, a) => ({
                ...map,
                [a.alarmId]: a,
            }), {} as AlarmMap);
    
            yield put(alarmReceived(alarmMap));
        }
    }
    if (refreshDevices) {
        yield put(deviceGroupsLoadRequest());
        yield put(devicesLoadRequest());
    }
    if (messages) {
        yield put(instantMessagesReceived(messages));
    }
}



// WEBPACK FOOTER //
// ./src/sagas/monitoring/pollSaga.ts